import { useCallback, useEffect, useRef, useState } from 'react';
import { useRevalidator } from 'react-router';
import { usePrevious } from '@mantine/hooks';

export function useRevalidateOnWindowFocus() {
  const shouldRefetch = useRef(false);
  const isVisible = usePageVisible();
  const prevIsVisible = usePrevious(isVisible);
  const { revalidate } = useRevalidator();

  useEffect(() => {
    if (
      prevIsVisible !== undefined &&
      prevIsVisible !== isVisible &&
      isVisible &&
      shouldRefetch.current
    ) {
      revalidate();
    }
    shouldRefetch.current = true;
  }, [isVisible, prevIsVisible, revalidate]);
}

function usePageVisible() {
  const [isVisible, setVisible] = useState(isDocumentVisible());
  const onFocus = useCallback(() => {
    setVisible(isDocumentVisible());
  }, []);
  useEffect(() => setupEventListeners(onFocus), [onFocus]);

  return isVisible;
}

function isDocumentVisible(): boolean {
  if (typeof document === 'undefined') {
    return true;
  }

  return document.visibilityState !== 'hidden';
}

function setupEventListeners(onFocus: () => void) {
  if (window) {
    window.addEventListener('visibilitychange', onFocus, false);
    window.addEventListener('focus', onFocus, false);

    return () => {
      window.removeEventListener('visibilitychange', onFocus);
      window.removeEventListener('focus', onFocus);
    };
  }
}
